import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { PaginatorModule } from 'primeng/paginator';
import { SelectModule } from 'primeng/select';
import * as L from 'leaflet';
import {
  IDiscoveryBootstrap,
  IDiscoveryCategory,
  IDiscoveryCenter,
  IDiscoveryCenterReview,
  IDiscoveryCenterReviewsResponse,
} from '../../_interfaces/IPatientSpace';
import { MapProviderAdapterService } from '../../_services/maps/map-provider-adapter.service';
import { PatientSpaceService } from '../../_services/patient-space/patient-space.service';

@Component({
  selector: 'app-patient-discover',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    InputTextModule,
    MultiSelectModule,
    PaginatorModule,
    SelectModule,
    ButtonModule,
    LeafletModule,
  ],
  templateUrl: './patient-discover.component.html',
  styleUrl: './patient-discover.component.scss',
})
export class PatientDiscoverComponent implements OnInit {
  loadingBootstrap = true;
  loadingCenters = false;
  loadingReviews = false;
  savingReview = false;

  bootstrap: IDiscoveryBootstrap = {
    categories: [],
    provinces: [],
  };

  filters = {
    category_ids: [] as number[],
    province: '',
    q: '',
    lat: null as number | null,
    lng: null as number | null,
    radius_km: 25,
  };

  centers: IDiscoveryCenter[] = [];
  page = 1;
  size = 20;
  total = 0;
  first = 0;

  selectedCenter: IDiscoveryCenter | null = null;
  showCenterDrawer = false;
  centerDrawerTab: 'summary' | 'reviews' = 'summary';
  selectedCenterReviews: IDiscoveryCenterReview[] = [];
  reviewsPage = 1;
  reviewsSize = 10;
  reviewsTotal = 0;
  selectedCenterReviewSummary: { average: number | null; count: number } = {
    average: null,
    count: 0,
  };
  ownReview = {
    rating: 5,
    comment: '',
    status: null as 'PENDING' | 'APPROVED' | 'REJECTED' | null,
  };
  readonly ratingStars = [1, 2, 3, 4, 5];

  mapOptions: L.MapOptions = {
    zoom: 6,
    center: L.latLng(40.4168, -3.7038),
  };
  mapLayers: L.Layer[] = [];
  private mapRef: L.Map | null = null;
  private markersLayer: L.LayerGroup = L.layerGroup();
  private locationLayer: L.LayerGroup = L.layerGroup();
  private readonly markerIcon = L.icon({
    iconRetinaUrl:
      'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl:
      'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl:
      'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  constructor(
    private readonly patientSpaceService: PatientSpaceService,
    private readonly mapProviderAdapter: MapProviderAdapterService,
    private readonly router: Router,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
  ) {}

  private isValidCoordinatePair(
    latitude: number | null | undefined,
    longitude: number | null | undefined,
  ): latitude is number {
    if (latitude === null || latitude === undefined) return false;
    if (longitude === null || longitude === undefined) return false;

    const lat = Number(latitude);
    const lng = Number(longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
    // Ignore sentinel coordinates frequently used as "unknown location".
    if (Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001) return false;

    return true;
  }

  private getTranslatedCategoryName(
    category: Pick<IDiscoveryCategory, 'code' | 'name'>,
  ): string {
    const code = `${category?.code || ''}`.trim();
    const fallback = `${category?.name || ''}`.trim();
    if (!code) {
      return fallback;
    }

    const key = `BUSINESS_CATEGORIES.${code}`;
    const translated = `${this.translate.instant(key) || ''}`.trim();
    if (!translated || translated === key) {
      return fallback;
    }
    return translated;
  }

  categoryLabel(category: Pick<IDiscoveryCategory, 'code' | 'name' | 'displayName'>): string {
    return `${category?.displayName || ''}`.trim() || this.getTranslatedCategoryName(category);
  }

  ngOnInit(): void {
    this.setupMapBaseLayer();
    this.patientSpaceService.discoveryBootstrap().subscribe({
      next: (bootstrap) => {
        this.bootstrap = {
          ...(bootstrap || { categories: [], provinces: [] }),
          categories: (bootstrap?.categories || []).map((category) => ({
            ...category,
            displayName: this.getTranslatedCategoryName(category),
          })),
        };
        this.filters.radius_km = Number(bootstrap?.defaults?.radius_km || 25);
        this.loadingBootstrap = false;
        this.loadCenters();
      },
      error: () => {
        this.loadingBootstrap = false;
      },
    });
  }

  private setupMapBaseLayer() {
    const tiles = this.mapProviderAdapter.getTilesConfig();
    this.mapLayers = [
      L.tileLayer(tiles.url, tiles.options),
      this.markersLayer,
      this.locationLayer,
    ];
  }

  onMapReady(map: L.Map) {
    this.mapRef = map;
    setTimeout(() => map.invalidateSize(), 50);
  }

  loadCenters(page = 1) {
    this.page = page;
    this.first = Math.max(0, (this.page - 1) * this.size);
    this.loadingCenters = true;
    this.selectedCenter = null;
    this.showCenterDrawer = false;
    this.selectedCenterReviews = [];
    this.reviewsTotal = 0;
    this.reviewsPage = 1;

    this.patientSpaceService
      .discoveryCenters({
        category_ids: this.filters.category_ids,
        province: this.filters.province || undefined,
        q: this.filters.q || undefined,
        lat: this.filters.lat || undefined,
        lng: this.filters.lng || undefined,
        radius_km:
          this.filters.lat !== null && this.filters.lng !== null
            ? this.filters.radius_km
            : undefined,
        page: this.page,
        size: this.size,
      })
      .subscribe({
        next: (response) => {
          this.centers = response?.data || [];
          this.total = Number(response?.total || 0);
          this.loadingCenters = false;
          this.renderMarkers();
        },
        error: () => {
          this.centers = [];
          this.total = 0;
          this.loadingCenters = false;
          this.renderMarkers();
        },
      });
  }

  onCentersPageChange(event: { page?: number; rows?: number }) {
    this.size = Number(event?.rows || this.size || 20);
    this.loadCenters(Number(event?.page || 0) + 1);
  }

  clearFilters() {
    this.filters = {
      category_ids: [],
      province: '',
      q: '',
      lat: null,
      lng: null,
      radius_km: Number(this.bootstrap?.defaults?.radius_km || 25),
    };
    this.loadCenters(1);
  }

  useMyLocation() {
    if (!navigator?.geolocation) {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.filters.lat = Number(position.coords.latitude);
        this.filters.lng = Number(position.coords.longitude);
        this.loadCenters(1);
      },
      () => {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('COMMON.WARNING'),
          detail: this.translate.instant('PATIENT_SPACE.DISCOVER.LOCATION_ERROR'),
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 8000,
      },
    );
  }

  removeMyLocation() {
    this.filters.lat = null;
    this.filters.lng = null;
    this.locationLayer.clearLayers();
    this.loadCenters(1);
  }

  private renderMarkers() {
    this.markersLayer.clearLayers();
    const coordinates = this.centers
      .map((center) => {
        const rawLat = center.latitude;
        const rawLng = center.longitude;
        if (!this.isValidCoordinatePair(rawLat, rawLng)) return null;
        const lat = Number(rawLat);
        const lng = Number(rawLng);
        return { center, lat, lng };
      })
      .filter((item): item is { center: IDiscoveryCenter; lat: number; lng: number } => !!item);

    for (const item of coordinates) {
      const marker = L.marker([item.lat, item.lng], {
        title: item.center.name,
        icon: this.markerIcon,
      });
      marker.bindPopup(`<strong>${item.center.name}</strong><br/>${item.center.address || ''}`);
      marker.on('click', () => this.selectCenter(item.center, 'summary'));
      this.markersLayer.addLayer(marker);
    }

    if (!this.mapRef) return;

    const hasLocation =
      this.filters.lat !== null &&
      this.filters.lng !== null &&
      Number.isFinite(Number(this.filters.lat)) &&
      Number.isFinite(Number(this.filters.lng));

    this.locationLayer.clearLayers();
    if (hasLocation) {
      const lat = Number(this.filters.lat);
      const lng = Number(this.filters.lng);
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        const radiusMeters = Math.max(1, Number(this.filters.radius_km || 25)) * 1000;
        const circle = L.circle([lat, lng], {
          radius: radiusMeters,
          color: '#2563eb',
          weight: 1,
          fillColor: '#3b82f6',
          fillOpacity: 0.08,
        });
        this.locationLayer.addLayer(circle);
        this.mapRef.setView([lat, lng], 11);
        return;
      }
    }

    if (!coordinates.length) {
      this.mapRef.setView([40.4168, -3.7038], 6);
      return;
    }

    const bounds = L.latLngBounds(coordinates.map((item) => [item.lat, item.lng] as [number, number]));
    this.mapRef.fitBounds(bounds.pad(0.2));
  }

  selectCenter(center: IDiscoveryCenter, tab: 'summary' | 'reviews' = 'summary') {
    this.selectedCenter = center;
    this.centerDrawerTab = tab;
    this.showCenterDrawer = true;
    this.reviewsPage = 1;
    this.ownReview = {
      rating: center.my_review?.rating || 5,
      comment: center.my_review?.comment || '',
      status: center.my_review?.status || null,
    };
    if (
      this.isValidCoordinatePair(center.latitude, center.longitude) &&
      this.mapRef
    ) {
      const lat = Number(center.latitude);
      const lng = Number(center.longitude);
      this.mapRef.flyTo([lat, lng], Math.max(this.mapRef.getZoom(), 13), {
        duration: 0.35,
      });
    }
    this.loadSelectedCenterReviews(1);
  }

  closeCenterDrawer() {
    this.showCenterDrawer = false;
    this.selectedCenter = null;
    this.selectedCenterReviews = [];
    this.selectedCenterReviewSummary = { average: null, count: 0 };
    this.reviewsTotal = 0;
  }

  loadSelectedCenterReviews(page = this.reviewsPage) {
    if (!this.selectedCenter?.id) return;
    this.reviewsPage = page;
    this.loadingReviews = true;
    this.patientSpaceService.discoveryCenterReviews(this.selectedCenter.id, {
      page: this.reviewsPage,
      size: this.reviewsSize,
    }).subscribe({
      next: (response: IDiscoveryCenterReviewsResponse) => {
        this.selectedCenterReviews = response?.data || [];
        this.reviewsTotal = Number(response?.total || 0);
        this.selectedCenterReviewSummary = {
          average: response?.summary?.average ?? null,
          count: Number(response?.summary?.count || 0),
        };
        if (response?.own_review) {
          this.ownReview = {
            rating: response.own_review.rating || 5,
            comment: response.own_review.comment || '',
            status: response.own_review.status || null,
          };
        }
        this.loadingReviews = false;
      },
      error: () => {
        this.selectedCenterReviews = [];
        this.reviewsTotal = 0;
        this.selectedCenterReviewSummary = { average: null, count: 0 };
        this.loadingReviews = false;
      },
    });
  }

  onReviewsPageChange(event: { page?: number; rows?: number }) {
    this.reviewsSize = Number(event?.rows || this.reviewsSize || 10);
    this.loadSelectedCenterReviews(Number(event?.page || 0) + 1);
  }

  saveReview() {
    if (!this.selectedCenter?.id || this.savingReview) return;
    this.savingReview = true;
    this.patientSpaceService
      .upsertDiscoveryCenterReview(this.selectedCenter.id, {
        rating: Number(this.ownReview.rating || 5),
        comment: `${this.ownReview.comment || ''}`.trim(),
      })
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('COMMON.SUCCESS'),
            detail: this.translate.instant('PATIENT_SPACE.DISCOVER.REVIEW.SAVED'),
          });
          this.savingReview = false;
          this.loadCenters(this.page);
          this.loadSelectedCenterReviews(this.reviewsPage);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('COMMON.ERROR'),
            detail: this.translate.instant('PATIENT_SPACE.DISCOVER.REVIEW.SAVE_ERROR'),
          });
          this.savingReview = false;
        },
      });
  }

  setOwnRating(star: number) {
    const safe = Number(star || 0);
    if (!Number.isFinite(safe)) return;
    this.ownReview.rating = Math.max(1, Math.min(5, Math.round(safe)));
  }

  getAverageRatingRounded(): number {
    const average = Number(this.selectedCenterReviewSummary?.average || 0);
    if (!Number.isFinite(average) || average <= 0) return 0;
    return Math.max(0, Math.min(5, Math.round(average)));
  }

  canReserve(center: IDiscoveryCenter): boolean {
    return center?.booking_available !== false && !!center?.booking_url;
  }

  reserve(center: IDiscoveryCenter) {
    if (!this.canReserve(center)) return;
    this.router.navigateByUrl(center.booking_url);
  }
}
